import React, { useMemo } from "react";
import { useDraggable } from "@dnd-kit/core";
import dayjs from "dayjs";
import { useProjectState } from "../../../../context/stateContext";
import "../../RestaurantSchemeComponent/index.css";

const BASE_SIZE = 70;

const SHAPE_MAP = {
  1: "round",
  2: "square",
  3: "rect",
};

const OBJECT_MAP = {
  4: { cls: "bar", icon: "🍸", baseW: 120, baseH: 50 },
  5: { cls: "wall", icon: "🧱", baseW: 140, baseH: 20 },
  6: { cls: "plant", icon: "🌿", baseW: 40, baseH: 40 },
  7: { cls: "entrance", icon: "🚪", baseW: 60, baseH: 30 },
  8: { cls: "stage", icon: "🎤", baseW: 160, baseH: 80 },
  9: { cls: "dj", icon: "🎧", baseW: 60, baseH: 60 },
};

const getChairPositions = (shape, seatsCount) => {
  const chairs = [];
  if (shape === 1) {
    for (let i = 0; i < seatsCount; i++) {
      const angle = (360 / seatsCount) * i - 90;
      const rad = (angle * Math.PI) / 180;
      chairs.push({
        top: `${50 + 55 * Math.sin(rad)}%`,
        left: `${50 + 55 * Math.cos(rad)}%`,
        transform: "translate(-50%, -50%)",
      });
    }
  } else if (shape === 2) {
    const sides = [
      { pos: "top", style: (i, n) => ({ top: "-10px", left: `${((i + 1) / (n + 1)) * 100}%`, transform: "translateX(-50%)" }) },
      { pos: "bottom", style: (i, n) => ({ bottom: "-10px", left: `${((i + 1) / (n + 1)) * 100}%`, transform: "translateX(-50%)" }) },
      { pos: "left", style: (i, n) => ({ left: "-10px", top: `${((i + 1) / (n + 1)) * 100}%`, transform: "translateY(-50%)" }) },
      { pos: "right", style: (i, n) => ({ right: "-10px", top: `${((i + 1) / (n + 1)) * 100}%`, transform: "translateY(-50%)" }) },
    ];
    let remaining = seatsCount;
    let sideIdx = 0;
    while (remaining > 0 && sideIdx < sides.length) {
      const perSide = Math.ceil(remaining / (sides.length - sideIdx));
      for (let i = 0; i < perSide && remaining > 0; i++) {
        chairs.push(sides[sideIdx].style(i, perSide));
        remaining--;
      }
      sideIdx++;
    }
  } else if (shape === 3) {
    const topCount = Math.ceil(seatsCount / 2);
    const bottomCount = seatsCount - topCount;
    for (let i = 0; i < topCount; i++) {
      chairs.push({ top: "-10px", left: `${((i + 1) / (topCount + 1)) * 100}%`, transform: "translateX(-50%)" });
    }
    for (let i = 0; i < bottomCount; i++) {
      chairs.push({ bottom: "-10px", left: `${((i + 1) / (bottomCount + 1)) * 100}%`, transform: "translateX(-50%)" });
    }
  }
  return chairs;
};

export function Draggable(props) {
  const {
    attributes,
    // listeners,
    setNodeRef,
    transform,
  } = useDraggable({
    id: props.id,
  });

  const { showSeatedTimeForReservationTable } = useProjectState();

  const isObject = props.shape >= 4;
  const objectDef = OBJECT_MAP[props.shape];

  const shapeClass = SHAPE_MAP[props.shape] || "round";
  const scale = props.scale || 1.0;
  const tableScale = scale * (props.scaleFactor || 1);

  let width, height;
  if (isObject && objectDef) {
    width = objectDef.baseW * tableScale;
    height = objectDef.baseH * tableScale;
  } else {
    const baseWidth = shapeClass === "rect" ? BASE_SIZE * 2.2 : BASE_SIZE;
    width = baseWidth * tableScale;
    height = BASE_SIZE * tableScale;
  }

  let statusClass = "";
  if (!isObject) {
    if (props.isBusy) {
      statusClass = "busy";
    } else if (props.nextReservation) {
      const currentTime = dayjs();
      const parsedGivenTime = dayjs(
        `${currentTime.format("YYYY-MM-DD")}T${props.nextReservation}`
      );
      const oneHourLater = currentTime.add(1.5, "hour");
      const isWithinOneHour = parsedGivenTime.isBefore(oneHourLater);
      if (isWithinOneHour) {
        statusClass = "reserved";
      }
    }
  }

  const chairs = useMemo(
    () => (isObject ? [] : getChairPositions(props.shape, props.seatsCount)),
    [props.shape, props.seatsCount, isObject]
  );

  const style = {
    position: "absolute",
    transform: transform
      ? `translate3d(${props.position.x + transform.x}px, ${
          props.position.y + transform.y
        }px, 0)`
      : `translate3d(${props.position.x}px, ${props.position.y}px, 0)`,
    cursor: "pointer",
    width: `${width}px`,
    height: `${height}px`,
  };

  function formatMinutesToTime(minutes) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    // Format hours and minutes to always be 2 digits
    const formattedHours = hours.toString().padStart(2, "0");
    const formattedMinutes = remainingMinutes.toString().padStart(2, "0");

    return `${formattedHours}:${formattedMinutes}`;
  }

  // Render decorative object (shape >= 4)
  if (isObject && objectDef) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        className="reservations-schema-draggable-container"
      >
        <div
          className={`reservations-schema-object-surface ${objectDef.cls}`}
          style={{
            transform: `rotate(${props.direction}deg)`,
            transition: "transform 0.3s ease",
          }}
        >
          {objectDef.icon}
        </div>
        <span className="reservations-schema-object-label">
          {props.tableName}
        </span>
      </div>
    );
  }

  // Render table (shape 1-3)
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      // {...listeners} // Disallow moving items
      className={`reservations-schema-draggable-container ${statusClass}`}
      onClick={(e) => {
        props.onTableClick(props.id);
      }}
    >
      <div
        className={`reservations-schema-table-surface ${shapeClass} ${statusClass}`}
        style={{
          transform: `rotate(${props.direction}deg)`,
          transition: "transform 0.3s ease",
        }}
      >
        {chairs.map((pos, idx) => (
          <div
            key={idx}
            className="reservations-schema-chair"
            style={pos}
          />
        ))}
      </div>
      <div className="reservations-schema-table-label">
        <span className="reservations-schema-table-name">
          {showSeatedTimeForReservationTable
            ? props.tableOpenDurationInMinutes !== null
              ? formatMinutesToTime(props.tableOpenDurationInMinutes)
              : props.tableName
            : props.tableName}
        </span>
        <span className="reservations-schema-table-seats">
          {props.seatsCount} м.
        </span>
      </div>
    </div>
  );
}
